import React, { useEffect, useRef } from 'react'
import { AxiosError } from 'axios'
import { useTheme } from 'next-themes'
import { get } from '../../api/request'
import { AuthState } from '../../types/auth'
import { useAuthState, useSetAuthState } from '../../contexts/AuthContext'
import { IMPERSONATION_TOKEN_KEY } from '../../utils/impersonationHandoff'

/** API error response structure */
interface ApiErrorResponse {
  message?: string
  error?: string
  statusCode?: number
}

interface AuthProviderProps {
  children: React.ReactNode
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const setAuth = useSetAuthState()
  const auth = useAuthState()
  const { setTheme } = useTheme()

  const authRef = useRef(auth)
  authRef.current = auth

  // Ensures the saved theme preference is applied only once per authenticated
  // session, so it doesn't clobber a manual toggle on subsequent renders.
  const hasAppliedThemeRef = useRef(false)

  useEffect(() => {
    const initializeAuth = async () => {
      const currentAuth = authRef.current
      if (currentAuth.isAuthenticated && currentAuth.user && currentAuth.token) {
        setAuth((prev: AuthState) => ({ ...prev, isLoading: false }))
        return
      }

      setAuth((prev: AuthState) => ({ ...prev, isLoading: true }))

      const impersonationToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
      const token = impersonationToken || localStorage.getItem('token')

      if (!token) {
        setAuth({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          pendingLoginCode: null,
        })
        return
      }

      try {
        const response = await get('/ui/users/me')
        const user = response.data

        setAuth({
          user: user,
          token: token,
          refreshToken: null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pendingLoginCode: null,
        })
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const status = axiosError.response?.status
        const message =
          axiosError.response?.data?.message || axiosError.message || 'Failed to fetch user data'

        // Only treat definitive auth failures as logged-out. Network blips and 5xx
        // after a successful in-app login (e.g. Operations OAuth) would otherwise
        // clear the session and bounce the user back to the provider login screen.
        if (status === 401 || status === 403) {
          setAuth({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
            pendingLoginCode: null,
          })
          return
        }

        setAuth((prev: AuthState) => {
          if (prev.isAuthenticated && prev.user && prev.token === token) {
            return {
              ...prev,
              isLoading: false,
              error: message,
            }
          }

          return {
            user: null,
            token: token,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
            pendingLoginCode: null,
          }
        })
      }
    }

    initializeAuth()
  }, [setAuth])

  useEffect(() => {
    // Apply the user's saved theme preference once, when they become
    // authenticated. When there is no saved preference, fall back to `system`
    // so next-themes tracks the OS setting. This must NOT re-run on every theme
    // change (next-themes recreates `setTheme` when the theme changes); doing so
    // would re-apply the stale saved value and revert the user's manual toggle,
    // since the toggle persists to the server without updating auth context.
    if (!auth.isAuthenticated || !auth.user) {
      hasAppliedThemeRef.current = false
      return
    }
    if (hasAppliedThemeRef.current) return
    hasAppliedThemeRef.current = true

    const userPreference = auth.user?.preferences?.theme?.mode as 'light' | 'dark' | undefined
    setTheme(userPreference ?? 'system')
  }, [auth.isAuthenticated, auth.user, setTheme])

  return <>{children}</>
}

export default AuthProvider
